import React, { useCallback, useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Switch,
  TextField,
  ThemeProvider,
  Typography,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SelectPaginate from 'app/shared/components/select-paginate/SelectPaginate';
import SelectPaginateFromPaginatedApi from 'app/shared/components/select-paginate/SelectPaginateFromPaginatedApi';
import { BoxForm, FormControlDefault, TextFieldForms, TypographyForms } from 'app/shared/layout/register/DefaultFormsComponents';
import { RegisterButton } from 'app/shared/layout/register/RegisterButton';
import { RegisterLayout } from 'app/shared/layout/register/RegisterLayout';
import { useAppSelector } from 'app/config/store';
import { ICameraDevice } from './camera-device.reducer';

interface CameraDeviceModel {
  id: number;
  name: string;
  productKey: string;
  defaultChannelCount: number;
}

interface ChannelConfig {
  channelNum: number;
  channelType: number; // 0 = video, 1 = audio
  channelName: string;
  active: boolean;
}

interface FormData {
  licensePlate: string;
  company: any;
  serialNumber: string;
  productKey: string;
  modelId: number | null;
  modelCode: string;
  channelCount: number;
  timeZone: string;
  active: boolean;
}

const initialFormData: FormData = {
  licensePlate: '',
  company: null,
  serialNumber: '',
  productKey: '808_D_SUB_sigaway_1786160467089',
  modelId: null,
  modelCode: '',
  channelCount: 4,
  timeZone: '-3',
  active: true,
};

const LIST_URL = '/camera-device';

const CameraDeviceUpdate = (props: RouteComponentProps<{ id?: string }>) => {
  const theme = useTheme();

  const editId = props.match.params.id;
  const isEditMode = Boolean(editId);
  const returnTruckImplementId = new URLSearchParams(props.location.search).get('returnTruckImplementId');
  const returnUrl =
    returnTruckImplementId && /^\d+$/.test(returnTruckImplementId) ? `/dashboards/truck-implement/${returnTruckImplementId}/devices-sensors` : LIST_URL;
  const listedDevices = useAppSelector(state => state.cameraDevice.entities) as ICameraDevice[];

  const [saving, setSaving] = useState(false);
  const [loadingModels, setLoadingModels] = useState(true);
  const [loadingDevice, setLoadingDevice] = useState(isEditMode);
  const [models, setModels] = useState<CameraDeviceModel[]>([]);
  const [formData, setFormData] = useState<FormData>(() => ({
    ...initialFormData,
    serialNumber: !isEditMode ? new URLSearchParams(props.location.search).get('serialNumber') || '' : '',
  }));
  const [showNewModelForm, setShowNewModelForm] = useState(false);
  const [newModelName, setNewModelName] = useState('');
  const [newModelChannels, setNewModelChannels] = useState(4);
  const [savingModel, setSavingModel] = useState(false);
  const [showChannelConfigForm, setShowChannelConfigForm] = useState(false);
  const [channels, setChannels] = useState<ChannelConfig[]>([]);

  useEffect(() => {
    axios
      .get('/api/admin/camera-device-models/active')
      .then(response => {
        const modelList = response.data || [];
        setModels(modelList);
      })
      .catch(() => toast.error('Não foi possível carregar os modelos de câmera.'))
      .then(() => setLoadingModels(false));
  }, []);

  const fillEditForm = useCallback((device: any) => {
    setFormData({
      licensePlate: device.licensePlate && device.licensePlate !== 'Não Vinculada' ? device.licensePlate : '',
      company: device.companyId ? { id: device.companyId, name: device.companyName } : null,
      serialNumber: device.serialNumber || '',
      productKey: device.productKey || initialFormData.productKey,
      modelId: null, // modelo não é editável, apenas como referência
      modelCode: device.deviceModelCode || '',
      channelCount: Array.isArray(device.deviceChannelList) ? device.deviceChannelList.length : initialFormData.channelCount,
      timeZone: device.timeZone != null ? String(device.timeZone) : initialFormData.timeZone,
      active: Boolean(device.active),
    });

    const existingChannels: ChannelConfig[] = Array.isArray(device.deviceChannelList)
      ? device.deviceChannelList.map((ch: any, idx: number) => ({
          channelNum: Number(ch.channelNum ?? idx + 1),
          channelType: ch.channelType ?? 0,
          channelName: ch.channelName || `Camera ${ch.channelNum ?? idx + 1}`,
          active: !device.disabledChannelNumbers?.includes(Number(ch.channelNum ?? idx + 1)),
        }))
      : [];
    setChannels(existingChannels);
  }, []);

  useEffect(() => {
    if (!isEditMode) return;
    let active = true;
    setLoadingDevice(true);

    const listedDevice = listedDevices.find(device => String(device.mappingId) === String(editId));
    if (listedDevice) {
      fillEditForm(listedDevice);
      setLoadingDevice(false);
      return () => {
        active = false;
      };
    }

    axios
      .get(`/api/admin/camera-devices/${editId}`)
      .then(response => {
        if (!active) return;
        const device = response.data;
        if (!device) {
          toast.error('Dispositivo de câmera não encontrado.');
          props.history.push(returnUrl);
          return;
        }

        fillEditForm(device);
      })
      .catch(() => {
        if (active) toast.error('Não foi possível carregar o dispositivo de câmera.');
      })
      .then(() => {
        if (active) setLoadingDevice(false);
      });

    return () => {
      active = false;
    };
  }, [editId, fillEditForm, isEditMode, listedDevices]);

  const handleCreateModel = async () => {
    if (!newModelName.trim()) {
      toast.error('Preencha o nome do modelo.');
      return;
    }
    if (!newModelChannels || newModelChannels < 1 || newModelChannels > 16) {
      toast.error('A quantidade de canais deve estar entre 1 e 16.');
      return;
    }

    setSavingModel(true);
    try {
      const { data: createdModel } = await axios.post<CameraDeviceModel>('/api/admin/camera-device-models', {
        name: newModelName.trim(),
        productKey: formData.productKey,
        defaultChannelCount: Number(newModelChannels),
        active: true,
      });

      setModels(current => [...current, createdModel]);
      setFormData(current => ({
        ...current,
        modelId: createdModel.id,
        channelCount: createdModel.defaultChannelCount,
        productKey: createdModel.productKey,
      }));
      const newChannels: ChannelConfig[] = [];
      for (let i = 1; i <= createdModel.defaultChannelCount; i += 1) {
        newChannels.push({ channelNum: i, channelType: 0, channelName: `Camera ${i}`, active: true });
      }
      setChannels(newChannels);

      toast.success('Modelo criado com sucesso.');
      setShowNewModelForm(false);
      setNewModelName('');
      setNewModelChannels(4);
    } catch (error: any) {
      const message = error?.response?.data?.detail || error?.response?.data?.title || 'Não foi possível criar o modelo.';
      toast.error(message);
    } finally {
      setSavingModel(false);
    }
  };

  const handleModelChange = (modelId: number | null) => {
    if (modelId === null) {
      setFormData(current => ({ ...current, modelId: null, channelCount: 4, productKey: '' }));
      setChannels([]);
      return;
    }
    const selectedModel = models.find(m => m.id === modelId);
    if (selectedModel) {
      setFormData(current => ({
        ...current,
        modelId: selectedModel.id,
        channelCount: selectedModel.defaultChannelCount,
        productKey: selectedModel.productKey,
      }));
      const newChannels: ChannelConfig[] = [];
      for (let i = 1; i <= selectedModel.defaultChannelCount; i++) {
        newChannels.push({
          channelNum: i,
          channelType: 0, // 0 = video
          channelName: `Camera ${i}`,
          active: true,
        });
      }
      setChannels(newChannels);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(current => ({ ...current, [field]: value }));
  };

  const handleChannelNameChange = (channelNum: number, newName: string) => {
    setChannels(current => current.map(ch => (ch.channelNum === channelNum ? { ...ch, channelName: newName.substring(0, 20) } : ch)));
  };

  const handleChannelActiveChange = (channelNum: number, active: boolean) => {
    setChannels(current => current.map(ch => (ch.channelNum === channelNum ? { ...ch, active } : ch)));
  };

  const handleChannelCountChange = (newCount: number) => {
    handleInputChange('channelCount', newCount);

    const newChannels: ChannelConfig[] = [];
    for (let i = 1; i <= newCount; i++) {
      const existing = channels.find(ch => ch.channelNum === i);
      newChannels.push(
        existing || {
          channelNum: i,
          channelType: 0,
          channelName: `Camera ${i}`,
          active: true,
        }
      );
    }
    setChannels(newChannels);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isEditMode) {
      setSaving(true);
      try {
        const payload = {
          active: true,
          deviceModelName: formData.modelCode,
          deviceChannelList: channels,
          disabledChannelNumbers: channels.filter(channel => !channel.active).map(channel => channel.channelNum),
        };
        const { data } = await axios.post(`/api/admin/camera-devices/${editId}/edit`, payload);
        if (data?.status === 0) {
          toast.success('Dispositivo de câmera atualizado com sucesso.');
          props.history.push(returnUrl);
        } else {
          toast.error(data?.msg || 'Não foi possível atualizar o dispositivo de câmera.');
        }
      } catch (error: any) {
        const message = error?.response?.data?.msg || error?.response?.data?.detail || 'Não foi possível atualizar o dispositivo de câmera.';
        toast.error(message);
      } finally {
        setSaving(false);
      }
      return;
    }

    if (!formData.company) {
      toast.error('Selecione uma empresa.');
      return;
    }

    if (!formData.licensePlate.trim()) {
      toast.error('Preencha a placa do veículo.');
      return;
    }

    if (!formData.serialNumber.trim()) {
      toast.error('Preencha o número de série.');
      return;
    }

    if (!formData.timeZone.trim()) {
      toast.error('Preencha o fuso horário.');
      return;
    }

    if (!formData.modelId) {
      toast.error('Selecione o modelo do dispositivo.');
      return;
    }

    if (!formData.channelCount || formData.channelCount < 1 || formData.channelCount > 16) {
      toast.error('A quantidade de canais deve estar entre 1 e 16.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        licensePlate: formData.licensePlate.trim(),
        companyId: formData.company.id,
        serialNumber: formData.serialNumber.trim(),
        productKey: formData.productKey,
        modelId: formData.modelId,
        channelCount: Number(formData.channelCount),
        timeZone: formData.timeZone.trim(),
        deviceChannelList: channels,
        disabledChannelNumbers: channels.filter(channel => !channel.active).map(channel => channel.channelNum),
      };

      await axios.post('/api/admin/camera-devices/quick-add', payload);
      toast.success('Dispositivo adicionado com sucesso.');
      setFormData(initialFormData);
      setChannels([]);
      props.history.push(LIST_URL);
    } catch (error: any) {
      const message = error?.response?.data?.detail || 'Não foi possível adicionar o dispositivo.';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (loadingModels || loadingDevice) {
    return (
      <ThemeProvider theme={theme}>
        <RegisterLayout>
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress color="secondary" />
          </Box>
        </RegisterLayout>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <RegisterLayout>
        <Box sx={{ width: { xs: '95%', sm: '70%', lg: '50%' } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', padding: '12px 0px 24px 0px', color: 'text.primary' }}>
            <Typography component={'strong'} variant="h5">
              {isEditMode ? 'Editar dispositivo de câmera' : 'Novo dispositivo de câmera'}
            </Typography>
          </Box>

          <BoxForm onSubmit={handleSubmit}>
            {/* Empresa */}
            <FormControlDefault variant="outlined" sx={{ mb: 1.5 }}>
              <TypographyForms sx={{ fontSize: '0.85rem' }}>Empresa</TypographyForms>
              <SelectPaginate
                apiUrl={'api/companies'}
                onChange={value => {
                  handleInputChange('company', value);
                  handleInputChange('licensePlate', '');
                }}
                value={formData.company}
                valueName="name"
                icon="sort-down"
                isRequired={true}
                filterStyle={true}
                isDisabled={isEditMode}
              />
            </FormControlDefault>

            <FormControlDefault variant="outlined" sx={{ mb: 1.5 }}>
              <TypographyForms sx={{ fontSize: '0.85rem' }}>Placa do Veículo</TypographyForms>
              <SelectPaginateFromPaginatedApi
                apiUrl={
                  formData.company?.id ? `api/truck-implements/license-plates?searchCompanyId=${formData.company.id}` : 'api/truck-implements/license-plates'
                }
                onChange={value => handleInputChange('licensePlate', value?.licensePlate || value?.id || '')}
                value={formData.licensePlate ? { id: formData.licensePlate, licensePlate: formData.licensePlate } : null}
                valueName="licensePlate"
                searchName="searchLicensePlate"
                icon="sort-down"
                isRequired={true}
                isDisabled={isEditMode || !formData.company?.id}
              />
            </FormControlDefault>

            <FormControlDefault variant="outlined" fullWidth sx={{ mb: 1.5 }}>
              <TypographyForms sx={{ fontSize: '0.85rem' }}>Número de Série</TypographyForms>
              <TextFieldForms
                value={formData.serialNumber}
                placeholder="Ex: H12345678"
                onChange={event => handleInputChange('serialNumber', event.target.value)}
                size="small"
                fullWidth
                sx={{ fontSize: '0.9rem' }}
                disabled={isEditMode}
              />
            </FormControlDefault>

            <FormControlDefault variant="outlined" fullWidth sx={{ mb: 1.5 }}>
              <TypographyForms sx={{ fontSize: '0.85rem' }}>
                Fuso Horário
                <Box component="span" sx={{ ml: 0.5, fontSize: '0.7rem', fontWeight: 400, color: 'text.secondary' }}>
                  (-13 a 14)
                </Box>
              </TypographyForms>
              <TextFieldForms
                value={formData.timeZone}
                type="number"
                placeholder="Ex: -3"
                inputProps={{ min: -12, max: 14 }}
                onChange={event => handleInputChange('timeZone', event.target.value)}
                size="small"
                fullWidth
                sx={{ fontSize: '0.9rem' }}
                disabled={isEditMode}
              />
            </FormControlDefault>

            <FormControlDefault variant="outlined" fullWidth sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: 24 }}>
                <TypographyForms sx={{ fontSize: '0.85rem' }}>Modelo do dispositivo</TypographyForms>
                {!isEditMode && (
                  <Button
                    variant="text"
                    color="secondary"
                    size="small"
                    startIcon={<AddIcon sx={{ fontSize: '1rem' }} />}
                    onClick={() => setShowNewModelForm(true)}
                    sx={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 0, p: 0 }}
                  >
                    Novo modelo
                  </Button>
                )}
              </Box>
              {isEditMode ? (
                <TextFieldForms value={formData.modelCode || '—'} size="small" fullWidth sx={{ fontSize: '0.9rem' }} disabled />
              ) : (
                <TextFieldForms
                  select
                  value={formData.modelId || ''}
                  onChange={event => handleModelChange(event.target.value ? Number(event.target.value) : null)}
                  size="small"
                  fullWidth
                  sx={{ fontSize: '0.9rem' }}
                  SelectProps={{ displayEmpty: true }}
                >
                  <MenuItem value="">Selecione o modelo</MenuItem>
                  {models.map(model => (
                    <MenuItem key={model.id} value={model.id} sx={{ fontSize: '0.9rem' }}>
                      {model.name} ({model.defaultChannelCount} {model.defaultChannelCount === 1 ? 'canal' : 'canais'})
                    </MenuItem>
                  ))}
                </TextFieldForms>
              )}
            </FormControlDefault>

            <FormControlDefault variant="outlined" fullWidth sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: 24 }}>
                <TypographyForms sx={{ fontSize: '0.85rem' }}>Qtd. de Canais de Vídeo</TypographyForms>
                <Button
                  variant="text"
                  color="secondary"
                  size="small"
                  startIcon={<EditIcon sx={{ fontSize: '1rem' }} />}
                  onClick={() => setShowChannelConfigForm(true)}
                  disabled={channels.length === 0}
                  sx={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 0, p: 0 }}
                >
                  Configurar canais
                </Button>
              </Box>
              <TextFieldForms
                value={formData.channelCount}
                type="number"
                placeholder="Ex: 4"
                inputProps={{ min: 1, max: 16 }}
                onChange={event => handleChannelCountChange(Number(event.target.value))}
                size="small"
                fullWidth
                sx={{ fontSize: '0.9rem' }}
                disabled={isEditMode}
              />
              {channels.length > 0 && (
                <Typography sx={{ mt: 0.75, fontSize: '0.75rem', color: 'text.secondary' }}>
                  Canais ativos:{' '}
                  {channels
                    .filter(channel => channel.active)
                    .map(channel => channel.channelName)
                    .join(', ') || 'Nenhum'}
                </Typography>
              )}
            </FormControlDefault>

            <RegisterButton disabled={saving} />
          </BoxForm>
        </Box>

        <Dialog open={showChannelConfigForm} onClose={() => setShowChannelConfigForm(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem' }}>Configurar canais</DialogTitle>
          <DialogContent sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              {channels.map(channel => (
                <Box key={channel.channelNum} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Typography sx={{ minWidth: '60px', fontSize: '0.9rem', fontWeight: 500 }}>Canal {channel.channelNum}:</Typography>
                  <TextField
                    value={channel.channelName}
                    onChange={e => handleChannelNameChange(channel.channelNum, e.target.value)}
                    placeholder={`Camera ${channel.channelNum}`}
                    size="small"
                    fullWidth
                    inputProps={{ maxLength: 20 }}
                    sx={{ fontSize: '0.9rem' }}
                  />
                  <Switch
                    checked={channel.active}
                    onChange={event => handleChannelActiveChange(channel.channelNum, event.target.checked)}
                    color="secondary"
                    inputProps={{ 'aria-label': `Ativar canal ${channel.channelNum}` }}
                  />
                  <Typography sx={{ minWidth: 55, fontSize: '0.8rem' }}>{channel.active ? 'Ativo' : 'Inativo'}</Typography>
                </Box>
              ))}
            </Box>
          </DialogContent>
          <DialogActions sx={{ gap: 1, p: 2 }}>
            <Button onClick={() => setShowChannelConfigForm(false)} variant="outlined" color="secondary">
              Cancelar
            </Button>
            <Button onClick={() => setShowChannelConfigForm(false)} variant="contained" color="secondary">
              Confirmar
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={showNewModelForm} onClose={() => setShowNewModelForm(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem' }}>Novo modelo de dispositivo</DialogTitle>
          <DialogContent sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <FormControlDefault variant="outlined" fullWidth sx={{ m: 0 }}>
                <TypographyForms sx={{ fontSize: '0.85rem' }}>Nome do Modelo</TypographyForms>
                <TextFieldForms
                  value={newModelName}
                  placeholder="Ex: AE-MN5043"
                  onChange={event => setNewModelName(event.target.value)}
                  size="small"
                  fullWidth
                  sx={{ fontSize: '0.9rem' }}
                />
              </FormControlDefault>

              <FormControlDefault variant="outlined" fullWidth sx={{ m: 0 }}>
                <TypographyForms sx={{ fontSize: '0.85rem' }}>Qtd. de Canais de Vídeo</TypographyForms>
                <TextFieldForms
                  value={newModelChannels}
                  type="number"
                  placeholder="Ex: 4"
                  inputProps={{ min: 1, max: 16 }}
                  onChange={event => setNewModelChannels(Number(event.target.value))}
                  size="small"
                  fullWidth
                  sx={{ fontSize: '0.9rem' }}
                />
              </FormControlDefault>
            </Box>
          </DialogContent>
          <DialogActions sx={{ gap: 1, p: 2 }}>
            <Button onClick={() => setShowNewModelForm(false)} variant="outlined" color="secondary" disabled={savingModel}>
              Cancelar
            </Button>
            <Button onClick={handleCreateModel} variant="contained" color="secondary" disabled={savingModel}>
              {savingModel ? <CircularProgress size={20} color="inherit" /> : 'Salvar'}
            </Button>
          </DialogActions>
        </Dialog>
      </RegisterLayout>
    </ThemeProvider>
  );
};

export default CameraDeviceUpdate;
